import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sofa, ArrowRight, Check, Clock, Shield, Star, Droplets, Bug, Baby } from 'lucide-react';
import { Link } from 'react-router-dom';

// Images
const HERO_IMAGE = "https://images.unsplash.com/photo-1758448511320-05d7d28f4298?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMG1vZGVybiUyMHNvZmElMjBsaXZpbmclMjByb29tJTIwbHV4dXJ5fGVufDB8fHx8MTc3NDExODc1NHww&ixlib=rb-4.1.0&q=85";
const CONTENT_IMAGE_1 = "https://images.unsplash.com/photo-1758448500596-ce0e0239f1be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMG1vZGVybiUyMHNvZmElMjBsaXZpbmclMjByb29tJTIwbHV4dXJ5fGVufDB8fHx8MTc3NDExODc1NHww&ixlib=rb-4.1.0&q=85";
const CONTENT_IMAGE_2 = "https://images.pexels.com/photos/1571467/pexels-photo-1571467.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const NettoyageCanapePage = () => {
  const questions = [
    { id: 'type_canape', label: 'Type de canapé ?', type: 'select', options: ['Tissu', 'Cuir', 'Microfibre', 'Velours', 'Alcantara', 'Autre'] },
    { id: 'nombre_places', label: 'Nombre de places ?', type: 'select', options: ['2 places', '3 places', '4+ places', 'Angle', 'Plusieurs canapés'] },
    { id: 'probleme', label: 'Problème principal ?', type: 'select', options: ['Taches', 'Odeurs', 'Acariens/Allergies', 'Usure générale', 'Tout'] },
    { id: 'urgence', label: 'Délai souhaité ?', type: 'select', options: ['Urgent (sous 48h)', 'Cette semaine', 'Flexible'] }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* ============================================
          PILIER 1: ATTENTION - Hero avec image luxe
          ============================================ */}
      <section className="hero-section relative min-h-[70vh] flex items-center">
        <img src={HERO_IMAGE} alt="Salon moderne avec canapé propre" className="hero-image-bg" />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Sofa className="h-10 w-10 text-white" />
              <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm text-sm">
                Homely Nettoyage
              </Badge>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white" style={{ fontFamily: 'var(--font-heading)' }}>
              Redonnez vie à votre canapé en 2 heures
            </h1>
            
            <p className="text-lg sm:text-xl text-white/90 mb-6 max-w-2xl">
              Nettoyage professionnel de canapé à domicile. 
              Taches, odeurs, acariens : nous éliminons tout pour un canapé comme neuf.
            </p>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Droplets className="h-5 w-5 text-blue-400" />
                <span className="text-white text-sm">Séchage rapide 4h</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Bug className="h-5 w-5 text-red-400" />
                <span className="text-white text-sm">Élimination acariens</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Baby className="h-5 w-5 text-pink-400" />
                <span className="text-white text-sm">Produits hypoallergéniques</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="btn-premium h-14 px-10 text-base rounded-full"
                onClick={() => document.getElementById('form').scrollIntoView({ behavior: 'smooth' })}
              >
                Devis gratuit en 2 min
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2 text-white">
                <span className="text-2xl font-bold">Dès 149 CHF</span>
                <span className="text-white/70">/ canapé 2 places</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 2: ÉMOTION - Le problème
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src={CONTENT_IMAGE_1} 
                alt="Salon moderne canapé" 
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>

            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Votre canapé cache plus que vous ne pensez
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6">
                Après quelques années d'utilisation, un canapé accumule en moyenne 
                <strong> 10 millions d'acariens</strong>, des bactéries, des cellules de peau 
                et des résidus alimentaires invisibles à l'œil nu.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: Bug, title: 'Acariens et allergènes', desc: 'Responsables d\'allergies, d\'eczéma et de problèmes respiratoires' },
                  { icon: Droplets, title: 'Taches incrustées', desc: 'Café, vin, nourriture... qui s\'incrustent dans les fibres' },
                  { icon: '🦠', title: 'Bactéries et odeurs', desc: 'Qui s\'accumulent avec le temps et dégagent des mauvaises odeurs' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-secondary/30 rounded-lg">
                    {typeof item.icon === 'string' ? (
                      <span className="text-2xl">{item.icon}</span>
                    ) : (
                      <item.icon className="h-6 w-6 text-[var(--sage)] flex-shrink-0 mt-1" />
                    )}
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-lg font-semibold" style={{ color: 'var(--sage-dark)' }}>
                Un nettoyage professionnel élimine 99,9% de ces nuisances.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 3: MÉMOIRE - Notre méthode
          ============================================ */}
      <section className="py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Notre méthode</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Un processus en 5 étapes pour un résultat parfait
            </h2>
            <div className="section-divider"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { step: '01', title: 'Diagnostic', desc: 'Analyse du tissu et des taches' },
              { step: '02', title: 'Aspiration', desc: 'Élimination poussières et acariens' },
              { step: '03', title: 'Pré-traitement', desc: 'Traitement ciblé des taches' },
              { step: '04', title: 'Injection-extraction', desc: 'Nettoyage en profondeur' },
              { step: '05', title: 'Séchage', desc: 'Séchage accéléré (4h max)' }
            ].map((item, idx) => (
              <div key={idx} className="card-premium p-6 text-center">
                <div className="text-3xl font-bold mb-3" style={{ color: 'var(--sage)' }}>{item.step}</div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Image résultat */}
          <div className="mt-16">
            <img 
              src={CONTENT_IMAGE_2} 
              alt="Résultat nettoyage canapé" 
              className="rounded-2xl shadow-xl w-full max-w-4xl mx-auto"
            />
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 4: DÉCISION - Tarifs transparents
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Tarifs transparents, sans surprise
            </h2>
            <div className="section-divider"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            {[
              { type: 'Canapé 2 places', price: '149', popular: false },
              { type: 'Canapé 3 places', price: '199', popular: true },
              { type: 'Canapé d\'angle', price: '279', popular: false }
            ].map((item, idx) => (
              <div key={idx} className={`card-premium p-8 text-center relative ${item.popular ? 'border-2 border-[var(--sage)]' : ''}`}>
                {item.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>
                    Le plus demandé
                  </Badge>
                )}
                <h3 className="text-xl font-semibold mb-4">{item.type}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">{item.price}</span>
                  <span className="text-muted-foreground"> CHF</span>
                </div>
                <ul className="text-sm text-muted-foreground space-y-2 mb-6">
                  <li className="flex items-center justify-center gap-2">
                    <Check className="h-4 w-4 text-green-500" /> Déplacement inclus
                  </li>
                  <li className="flex items-center justify-center gap-2">
                    <Check className="h-4 w-4 text-green-500" /> Produits inclus
                  </li>
                  <li className="flex items-center justify-center gap-2">
                    <Check className="h-4 w-4 text-green-500" /> Garantie satisfaction
                  </li>
                </ul>
                <Button 
                  className={item.popular ? 'btn-premium w-full' : 'w-full'} 
                  variant={item.popular ? 'default' : 'outline'}
                  onClick={() => document.getElementById('form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Réserver
                </Button>
              </div>
            ))}
          </div>

          {/* Avis */}
          <div className="bg-secondary/30 p-8 rounded-2xl max-w-3xl mx-auto text-center">
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-lg italic mb-4">
              "Mon canapé avait 8 ans et je pensais devoir le changer. 
              Après le nettoyage, il est comme neuf ! Incroyable."
            </p>
            <p className="font-semibold">— Christine B., Lausanne</p>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 5: ACTION - Formulaire
          ============================================ */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} id="form">
        <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Demandez votre devis gratuit
            </h2>
            <p className="text-white/70">Réponse garantie sous 24h • Intervention possible sous 48h</p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <ServiceLeadForm service="nettoyage" pole="nettoyage" questions={questions} />
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <h3 className="font-semibold mb-4">Autres services de nettoyage</h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/nettoyage/terrasse" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Nettoyage terrasse</Link>
            <Link to="/nettoyage/vitres" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Nettoyage vitres</Link>
            <Link to="/nettoyage/fin-de-chantier" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Fin de chantier</Link>
            <Link to="/nettoyage/apres-bail" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Après bail</Link>
            <Link to="/nettoyage" className="text-sm px-4 py-2 border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">Tous nos services</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
